import { MortgageData } from '../../CalculatorTabs';
import { calculateMortgagePayment } from './mortgageUtils';
import { createCreditScoreImprovementComparison, createRefinancingComparison, createExtraPaymentComparison, PITIComparison, formatCurrency } from '@/utils/PITIComparison';

export interface BuyingPowerData {
  totalMonthlyIncome: number;
  totalMonthlyDebt: number;
  maxBuyingPower: number;
  debtToIncomeRatio: number;
  creditScore: number;
  downPaymentPercentage: number;
  state: string;
  loanType: string;
}

export interface OptimizationStrategy {
  title: string;
  description: string;
  impact: string;
  savings: string;
  confidence: 'high' | 'medium' | 'low';
  priority: number;
  category: 'payment' | 'refinance' | 'credit' | 'equity' | 'tax';
  pitiComparison?: PITIComparison;
}

export interface PersonalizedOptimization {
  extraPaymentAnalysis: {
    recommendedAmount: number;
    interestSavings: number;
    yearsReduced: number;
    affordabilityRatio: number;
  };
  refinancingAnalysis: {
    potentialRateReduction: number;
    breakEvenPoint: number;
    monthlySavings: number;
    isRecommended: boolean;
  };
  creditOptimization: {
    currentImpact: string;
    potentialImprovement: string;
    timeframe: string;
  };
  strategies: OptimizationStrategy[];
}

// Calculate optimal extra payment based on user's financial capacity
export const calculateOptimalExtraPayment = (
  mortgageData: MortgageData,
  buyingPowerData: BuyingPowerData,
  selectedTerm: string
): { recommendedAmount: number; interestSavings: number; yearsReduced: number; affordabilityRatio: number } => {
  // Safety check for valid income data
  if (buyingPowerData.totalMonthlyIncome <= 0) {
    return {
      recommendedAmount: 0,
      interestSavings: 0,
      yearsReduced: 0,
      affordabilityRatio: 0
    };
  }
  
  const loanAmount = mortgageData.homePrice - mortgageData.downPayment;
  const monthlyPayment = calculateMortgagePayment(loanAmount, mortgageData.interestRate, parseInt(selectedTerm));
  
  // Calculate available cash flow (20% buffer for safety)
  const availableCashFlow = Math.max(0, (buyingPowerData.totalMonthlyIncome - buyingPowerData.totalMonthlyDebt - monthlyPayment) * 0.8);
  
  // Recommend 10-25% of available cash flow for extra payments
  const baseRecommendation = Math.max(50, Math.min(500, availableCashFlow * 0.15));
  const recommendedAmount = Math.round(baseRecommendation / 25) * 25; // Round to nearest $25
  
  // Calculate savings with recommended extra payment
  const termMonths = parseInt(selectedTerm) * 12;
  const monthlyRate = mortgageData.interestRate / 100 / 12;
  
  // Standard loan total interest
  const standardTotalInterest = (monthlyPayment * termMonths) - loanAmount;
  
  // Calculate with extra payment using amortization formula
  let balance = loanAmount;
  let totalPaid = 0;
  let months = 0;
  
  while (balance > 0 && months < termMonths) {
    const interestPayment = balance * monthlyRate;
    const principalPayment = monthlyPayment - interestPayment + recommendedAmount;
    balance = Math.max(0, balance - principalPayment);
    totalPaid += monthlyPayment + recommendedAmount;
    months++;
  }
  
  const interestSavings = Math.max(0, standardTotalInterest - (totalPaid - loanAmount));
  const yearsReduced = Math.max(0, (termMonths - months) / 12);
  const affordabilityRatio = availableCashFlow > 0 ? (recommendedAmount / availableCashFlow) : 0;
  
  return {
    recommendedAmount,
    interestSavings: Math.round(interestSavings),
    yearsReduced: Math.round(yearsReduced * 10) / 10,
    affordabilityRatio: Math.round(affordabilityRatio * 100) / 100
  };
};

// Analyze refinancing opportunities based on credit score and market conditions
export const analyzeRefinancingOpportunity = (
  mortgageData: MortgageData,
  buyingPowerData: BuyingPowerData,
  selectedTerm: string
): { potentialRateReduction: number; breakEvenPoint: number; monthlySavings: number; isRecommended: boolean } => {
  const loanAmount = mortgageData.homePrice - mortgageData.downPayment;
  const currentPayment = calculateMortgagePayment(loanAmount, mortgageData.interestRate, parseInt(selectedTerm));
  
  // Estimate potential rate reduction based on credit score
  let potentialRateReduction = 0;
  if (buyingPowerData.creditScore >= 760) {
    potentialRateReduction = 0.5; // Excellent credit
  } else if (buyingPowerData.creditScore >= 720) {
    potentialRateReduction = 0.25; // Good credit
  } else if (buyingPowerData.creditScore >= 680) {
    potentialRateReduction = 0.125; // Fair credit
  }
  
  const newRate = Math.max(3.0, mortgageData.interestRate - potentialRateReduction);
  const newPayment = calculateMortgagePayment(loanAmount, newRate, parseInt(selectedTerm));
  const monthlySavings = Math.max(0, currentPayment - newPayment);
  
  // Estimate closing costs (2% of loan amount)
  const closingCosts = loanAmount * 0.02;
  const breakEvenPoint = monthlySavings > 0 ? Math.round(closingCosts / monthlySavings) : 999;
  
  return {
    potentialRateReduction,
    breakEvenPoint,
    monthlySavings: Math.round(monthlySavings),
    isRecommended: breakEvenPoint <= 36 && monthlySavings >= 100 // Recommend if break-even <= 3 years
  };
};

// Generate credit score optimization recommendations
export const analyzeCreditOptimization = (
  buyingPowerData: BuyingPowerData,
  mortgageData: MortgageData
): { currentImpact: string; potentialImprovement: string; timeframe: string; pitiComparison?: PITIComparison } => {
  const currentScore = buyingPowerData.creditScore;
  let targetScore = currentScore;
  let pitiComparison: PITIComparison | undefined;
  
  if (currentScore >= 760) {
    return {
      currentImpact: "Excellent credit - you're getting the best rates available",
      potentialImprovement: "Maintain current habits to preserve excellent credit",
      timeframe: "Ongoing maintenance"
    };
  } else if (currentScore >= 720) {
    targetScore = 760;
    pitiComparison = createCreditScoreImprovementComparison(mortgageData, currentScore, targetScore);
    return {
      currentImpact: "Good credit with competitive rates",
      potentialImprovement: `Improving to 760+ could save ${formatCurrency(pitiComparison.savings.monthly)}/month`,
      timeframe: "6-12 months with focused effort",
      pitiComparison
    };
  } else if (currentScore >= 680) {
    targetScore = 720;
    pitiComparison = createCreditScoreImprovementComparison(mortgageData, currentScore, targetScore);
    return {
      currentImpact: "Fair credit - paying higher interest rates",
      potentialImprovement: `Improving to 720+ could save ${formatCurrency(pitiComparison.savings.monthly)}/month`,
      timeframe: "12-18 months with credit optimization",
      pitiComparison
    };
  } else {
    targetScore = 680;
    pitiComparison = createCreditScoreImprovementComparison(mortgageData, currentScore, targetScore);
    return {
      currentImpact: "Credit score significantly impacting your rate",
      potentialImprovement: `Improving to 680+ could save ${formatCurrency(pitiComparison.savings.monthly)}/month`,
      timeframe: "18-24 months with aggressive credit repair",
      pitiComparison
    };
  }
};

// Generate personalized optimization strategies
export const generatePersonalizedStrategies = (
  mortgageData: MortgageData,
  buyingPowerData: BuyingPowerData,
  selectedTerm: string
): OptimizationStrategy[] => {
  const strategies: OptimizationStrategy[] = [];
  const loanAmount = mortgageData.homePrice - mortgageData.downPayment;
  
  // Extra payment strategy
  const extraPaymentAnalysis = calculateOptimalExtraPayment(mortgageData, buyingPowerData, selectedTerm);
  if (extraPaymentAnalysis.affordabilityRatio <= 0.3) {
    const extraPaymentComparison = createExtraPaymentComparison(mortgageData, extraPaymentAnalysis.recommendedAmount, parseInt(selectedTerm));
    strategies.push({
      title: "Smart Extra Principal Payments",
      description: `Add $${extraPaymentAnalysis.recommendedAmount}/month to principal based on your ${Math.round((1 - extraPaymentAnalysis.affordabilityRatio) * 100)}% available cash flow`,
      impact: `Save ${formatCurrency(extraPaymentComparison.interestSavings)} in total interest`,
      savings: `Pay off loan ${extraPaymentComparison.yearsReduced.toFixed(1)} years early`,
      confidence: extraPaymentAnalysis.affordabilityRatio <= 0.2 ? 'high' : 'medium',
      priority: 1,
      category: 'payment',
      pitiComparison: extraPaymentComparison.comparison
    });
  }
  
  // Refinancing strategy
  const refinanceAnalysis = analyzeRefinancingOpportunity(mortgageData, buyingPowerData, selectedTerm);
  if (refinanceAnalysis.isRecommended) {
    const newRate = Math.max(3.0, mortgageData.interestRate - refinanceAnalysis.potentialRateReduction);
    const closingCosts = loanAmount * 0.02;
    const refinancingComparison = createRefinancingComparison(mortgageData, newRate, closingCosts, parseInt(selectedTerm));
    
    strategies.push({
      title: "Strategic Refinancing Opportunity",
      description: `Your ${buyingPowerData.creditScore} credit score qualifies you for ${refinanceAnalysis.potentialRateReduction}% rate reduction`,
      impact: `Save ${formatCurrency(refinancingComparison.savings.monthly)}/month`,
      savings: `Break-even in ${refinancingComparison.breakEvenMonths} months, lifetime savings: ${formatCurrency(refinancingComparison.savings.lifetime)}`,
      confidence: refinanceAnalysis.breakEvenPoint <= 24 ? 'high' : 'medium',
      priority: 2,
      category: 'refinance',
      pitiComparison: refinancingComparison
    });
  }
  
  // Credit optimization strategy
  if (buyingPowerData.creditScore < 760) {
    const creditAnalysis = analyzeCreditOptimization(buyingPowerData, mortgageData);
    strategies.push({
      title: "Credit Score Optimization",
      description: creditAnalysis.potentialImprovement,
      impact: creditAnalysis.pitiComparison ? `Save ${formatCurrency(creditAnalysis.pitiComparison.savings.monthly)}/month on current loan` : "Lower interest rates on future loans",
      savings: `Timeline: ${creditAnalysis.timeframe}${creditAnalysis.pitiComparison ? `, Lifetime savings: ${formatCurrency(creditAnalysis.pitiComparison.savings.lifetime)}` : ''}`,
      confidence: buyingPowerData.creditScore >= 680 ? 'high' : 'medium',
      priority: 3,
      category: 'credit',
      pitiComparison: creditAnalysis.pitiComparison
    });
  }
  
  // PMI removal strategy (for conventional loans with PMI)
  if (mortgageData.loanType === 'conventional' && mortgageData.pmi > 0) {
    const currentLTV = (loanAmount / mortgageData.homePrice) * 100;
    if (currentLTV > 78) {
      strategies.push({
        title: "PMI Elimination Strategy",
        description: `Pay down to 78% LTV or wait for appreciation to eliminate $${mortgageData.pmi}/month PMI`,
        impact: `Save $${mortgageData.pmi * 12}/year`,
        savings: "Immediate monthly savings once achieved",
        confidence: 'high',
        priority: 4,
        category: 'equity'
      });
    }
  }
  
  // State-specific strategies
  if (['CA', 'NY', 'TX', 'FL'].includes(buyingPowerData.state)) {
    strategies.push({
      title: "Property Tax Appeal Opportunity",
      description: `${buyingPowerData.state} has active property tax appeal programs - review your assessment`,
      impact: "Potential 5-15% property tax reduction",
      savings: `Save $${Math.round(mortgageData.propertyTax * 0.1 * 12)}-${Math.round(mortgageData.propertyTax * 0.15 * 12)}/year`,
      confidence: 'medium',
      priority: 5,
      category: 'tax'
    });
  }
  
  return strategies.sort((a, b) => a.priority - b.priority);
};

export const generatePersonalizedOptimization = (
  mortgageData: MortgageData,
  buyingPowerData: BuyingPowerData,
  selectedTerm: string
): PersonalizedOptimization => {
  return {
    extraPaymentAnalysis: calculateOptimalExtraPayment(mortgageData, buyingPowerData, selectedTerm),
    refinancingAnalysis: analyzeRefinancingOpportunity(mortgageData, buyingPowerData, selectedTerm),
    creditOptimization: analyzeCreditOptimization(buyingPowerData, mortgageData),
    strategies: generatePersonalizedStrategies(mortgageData, buyingPowerData, selectedTerm)
  };
};